import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Hash, Trash2, ExternalLink, Grid, Bookmark, CheckSquare, Search, StickyNote, Calendar, X, Heart, MessageCircle, Play } from 'lucide-react';
import api from '../services/api';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { toast } from 'react-toastify';
import { useConfirm } from '../hooks/useConfirm';
import { useTheme } from '../contexts/ThemeContext';

// Fora do componente principal pra React.memo funcionar de verdade
const PostCard = React.memo(({ post, isSaved, onSave, onUnsave, onNota, darkMode }) => {
  const [playVideo, setPlayVideo] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const url = post.media_url || post.mediaUrl;
  const isVideo = (post.media_type || post.mediaType) === 'VIDEO';
  const isCarousel = (post.media_type || post.mediaType) === 'CAROUSEL_ALBUM';
  const saved = post.salvo || isSaved;

  useEffect(() => {
    const el = ref.current;
    if (!el || !isVideo) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { rootMargin: '400px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [isVideo]);

  return (
    <div className={`border rounded-lg overflow-hidden ${saved ? 'border-green-400 dark:border-green-600 ring-1 ring-green-200 dark:ring-green-800' : 'border-gray-200 dark:border-gray-700'}`}>
      {(url || isCarousel) && (
        <div ref={ref} className="relative bg-black aspect-[4/5]">
          {isVideo ? (
            playVideo ? (
              <video src={url} className="w-full h-full object-contain" controls autoPlay />
            ) : visible ? (
              <div className="relative w-full h-full cursor-pointer" onClick={() => setPlayVideo(true)}>
                <video src={url} className="w-full h-full object-contain" preload="metadata" muted playsInline />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
                    <Play size={28} className="text-white ml-1" fill="white" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Play size={28} className="text-gray-600" />
              </div>
            )
          ) : url ? (
            <img src={url} alt="" className="w-full h-full object-contain" loading="lazy" decoding="async" />
          ) : (
            <a href={post.permalink} target="_blank" rel="noopener noreferrer" className="w-full h-full flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-gray-300">
              <Grid size={32} />
              <span className="text-xs">Ver carrossel no Instagram</span>
            </a>
          )}
          {isCarousel && url && <Grid size={18} className="absolute top-2 left-2 text-white drop-shadow" />}
          {saved && <span className="absolute top-2 right-2 bg-green-600 text-white text-xs px-2 py-0.5 rounded-full">Salvo</span>}
        </div>
      )}
      <div className="p-3">
        {post.caption && <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 line-clamp-3">{post.caption}</p>}
        {post.nota && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-2 mb-2">
            <p className="text-xs text-yellow-700 dark:text-yellow-300"><StickyNote size={12} className="inline mr-1" />{post.nota}</p>
          </div>
        )}
        <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
          <span>{(post.timestamp || post.postTimestamp) ? new Date(post.timestamp || post.postTimestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><Heart size={12} className="text-red-400" /> {post.like_count ?? post.likeCount ?? 0}</span>
            <span className="flex items-center gap-1"><MessageCircle size={12} /> {post.comments_count ?? post.commentsCount ?? 0}</span>
          </div>
        </div>
        <div className="flex gap-1.5">
          {post.permalink && (
            <a href={post.permalink} target="_blank" rel="noopener noreferrer"
              className="flex-1 px-2 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center gap-1 text-xs">
              <ExternalLink size={14} /> Ver
            </a>
          )}
          {!isSaved && !post.salvo && (
            <button onClick={() => onSave(post)}
              className="px-2 py-1.5 bg-blue-600 text-white rounded flex items-center gap-1 text-xs hover:bg-blue-700">
              <Bookmark size={14} /> Salvar
            </button>
          )}
          {(isSaved || post.salvo) && !isSaved && (
            <span className="px-2 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded flex items-center gap-1 text-xs">
              <CheckSquare size={14} /> Salvo
            </span>
          )}
          {isSaved && (
            <>
              <button onClick={() => onNota(post)}
                className="px-2 py-1.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded text-xs">
                <StickyNote size={14} />
              </button>
              <button onClick={() => onUnsave(post.id)}
                className="px-2 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-xs">
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default function HashtagMonitoring() {
  const { darkMode } = useTheme();
  const showConfirm = useConfirm();
  const [monitors, setMonitors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [posts, setPosts] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);
  const [newHashtag, setNewHashtag] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [tab, setTab] = useState('recent');
  const [searchText, setSearchText] = useState('');
  const [notaModal, setNotaModal] = useState(null);
  const [notaText, setNotaText] = useState('');

  const cursorRef = useRef(null);
  const loadingRef = useRef(false);
  const tabRef = useRef(tab);
  const fetchRef = useRef(null);

  useEffect(() => { loadMonitors(); }, []);

  const fetchFromMeta = useCallback(async (append = false) => {
    if (!selected) return;
    if (!append) setLoading(true); else setLoadingMore(true);
    loadingRef.current = true;
    try {
      const endpoint = tab === 'top' ? 'search-top' : 'search';
      const res = await api.post(`/instagram-monitoring/hashtags/${selected.id}/${endpoint}`, {
        cursor: append ? cursorRef.current : null,
      });
      const newPosts = res.data.posts || [];
      if (append && newPosts.length === 0) {
        setCursor(null); cursorRef.current = null; return;
      }
      setPosts(prev => {
        if (!append) return newPosts;
        const existingIds = new Set(prev.map(p => p.id));
        return [...prev, ...newPosts.filter(p => !existingIds.has(p.id))];
      });
      setCursor(res.data.nextCursor);
      cursorRef.current = res.data.nextCursor;
    } catch { toast.error('Erro ao buscar posts'); }
    finally { setLoading(false); setLoadingMore(false); loadingRef.current = false; }
  }, [selected, tab]);

  useEffect(() => { fetchRef.current = fetchFromMeta; }, [fetchFromMeta]);

  // Scroll infinito
  useEffect(() => {
    const handleScroll = () => {
      if (loadingRef.current || !cursorRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollTop + clientHeight >= scrollHeight - 300) fetchRef.current?.(true);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    tabRef.current = tab;
    if (selected && tab !== 'saved') {
      setPosts([]); setCursor(null); cursorRef.current = null;
      fetchFromMeta(false);
    }
    if (selected && tab === 'saved') loadSaved();
  }, [selected, tab]);

  const loadSaved = async () => {
    if (!selected) return;
    try { const res = await api.get(`/instagram-monitoring/hashtags/${selected.id}/saved`); setSavedPosts(res.data || []); } catch {}
  };

  const loadMonitors = async () => {
    try { const res = await api.get('/instagram-monitoring/hashtags'); setMonitors(res.data); }
    catch { toast.error('Erro ao carregar hashtags'); }
  };

  const addHashtag = async (e) => {
    e.preventDefault();
    if (!newHashtag.trim()) return;
    try {
      const res = await api.post('/instagram-monitoring/hashtags', { hashtag: newHashtag.replace('#', '') });
      setNewHashtag(''); loadMonitors(); setSelected(res.data);
      toast.success('Hashtag adicionada!');
    } catch { toast.error('Erro ao adicionar'); }
  };

  const deleteMonitor = async (id) => {
    if (!await showConfirm({ title: 'Remover', message: 'Remover monitoramento e posts salvos?', confirmText: 'Remover', cancelText: 'Cancelar' })) return;
    try {
      await api.delete(`/instagram-monitoring/hashtags/${id}`);
      if (selected?.id === id) { setSelected(null); setPosts([]); }
      loadMonitors(); toast.success('Removido');
    } catch { toast.error('Erro'); }
  };

  const savePost = useCallback(async (post) => {
    try {
      const res = await api.post(`/instagram-monitoring/hashtags/${selected.id}/save-post`, post);
      setPosts(prev => prev.map(p => p.id === post.id ? { ...p, salvo: true } : p));
      setSavedPosts(prev => [...prev, res.data]);
      setMonitors(prev => prev.map(m => m.id === selected.id ? { ...m, totalSalvos: (m.totalSalvos || 0) + 1 } : m));
      toast.success('Post salvo!');
    } catch { toast.error('Erro ao salvar'); }
  }, [selected]);

  const unsavePost = useCallback(async (postId) => {
    try {
      await api.delete(`/instagram-monitoring/posts/${postId}`);
      setSavedPosts(prev => prev.filter(p => p.id !== postId));
      setMonitors(prev => prev.map(m => m.id === selected.id ? { ...m, totalSalvos: Math.max(0, (m.totalSalvos || 1) - 1) } : m));
      toast.success('Removido dos salvos');
    } catch { toast.error('Erro'); }
  }, [selected]);

  const openNota = useCallback((post) => { setNotaModal(post); setNotaText(post.nota || ''); }, []);

  const saveNota = async () => {
    if (!notaModal) return;
    try {
      await api.post(`/instagram-monitoring/posts/${notaModal.id}/nota`, { nota: notaText });
      setSavedPosts(prev => prev.map(p => p.id === notaModal.id ? { ...p, nota: notaText } : p));
      setNotaModal(null); toast.success('Nota salva');
    } catch { toast.error('Erro'); }
  };

  const filtered = searchText
    ? posts.filter(p => p.caption && p.caption.toLowerCase().includes(searchText.toLowerCase()))
    : posts;

  const sorted = filtered;

  const grouped = tab === 'recent' ? sorted.reduce((acc, post) => {
    const key = post.timestamp
      ? new Date(post.timestamp).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })
      : 'Sem data';
    (acc[key] = acc[key] || []).push(post);
    return acc;
  }, {}) : {};

  const renderGrid = (items, isSaved = false) => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {items.map(p => <PostCard key={p.id} post={p} isSaved={isSaved} onSave={savePost} onUnsave={unsavePost} onNota={openNota} darkMode={darkMode} />)}
    </div>
  );

  return (
    <Layout>
      <PageHeader title="Insights — Hashtags" subtitle="Descubra tendências e oportunidades no Instagram" />
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <form onSubmit={addHashtag} className="flex gap-2 mb-4">
                <input type="text" value={newHashtag} onChange={(e) => setNewHashtag(e.target.value)}
                  placeholder="Nova hashtag"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                <button type="submit" className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"><Plus size={18} /></button>
              </form>
              <div className="space-y-1.5">
                {monitors.map((m) => (
                  <div key={m.id}
                    className={`p-3 rounded-lg cursor-pointer flex items-center justify-between group ${selected?.id === m.id ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500' : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                    onClick={() => { setSelected(m); setTab('recent'); }}>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <Hash size={16} className="text-blue-600" />
                        <span className="font-medium text-gray-900 dark:text-white text-sm">{m.hashtag}</span>
                      </div>
                      <span className="text-xs text-gray-400">{m.totalSalvos || 0} salvos</span>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); deleteMonitor(m.id); }}
                      className="text-red-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={15} /></button>
                  </div>
                ))}
                {monitors.length === 0 && <p className="text-gray-400 text-sm text-center py-6">Adicione uma hashtag</p>}
              </div>
            </div>
          </div>

          {/* Main */}
          <div className="lg:col-span-3">
            {selected ? (
              <div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">#{selected.hashtag}</h3>
                    <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
                      {[
                        { key: 'recent', label: 'Recentes' },
                        { key: 'top', label: 'Relevância' },
                        { key: 'saved', label: `Salvos (${savedPosts.length})` },
                      ].map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === t.key ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {tab !== 'saved' && (
                    <div className="flex items-center gap-2">
                      <Search size={16} className="text-gray-400" />
                      <input type="text" value={searchText} onChange={(e) => setSearchText(e.target.value)}
                        placeholder="Filtrar por texto na legenda..."
                        className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                      {searchText && (
                        <>
                          <span className="text-xs text-gray-400">{sorted.length} resultados</span>
                          <button onClick={() => setSearchText('')} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  {tab === 'saved' ? (
                    savedPosts.length > 0 ? renderGrid(savedPosts, true) : (
                      <div className="text-center py-12">
                        <Bookmark size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                        <p className="text-gray-500 dark:text-gray-400">Nenhum post salvo ainda</p>
                      </div>
                    )
                  ) : loading ? (
                    <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" /></div>
                  ) : sorted.length > 0 ? (
                    <>
                      {tab === 'top' ? renderGrid(sorted) : (
                        Object.entries(grouped).map(([date, dayPosts]) => (
                          <div key={date} className="mb-6 last:mb-0">
                            <div className="flex items-center gap-3 mb-3">
                              <Calendar size={14} className="text-gray-400" />
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-white capitalize">{date}</h4>
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 px-2 py-0.5 rounded-full">{dayPosts.length}</span>
                              <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                            </div>
                            {renderGrid(dayPosts)}
                          </div>
                        ))
                      )}
                      <div className="py-4 text-center">
                        {loadingMore && <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />}
                        {!cursor && !loadingMore && sorted.length > 0 && (
                          <div className="text-center text-gray-400 text-xs mt-2 space-y-1">
                            <p className="font-medium">{sorted.length} posts exibidos</p>
                            <p>O Instagram disponibiliza apenas os posts mais recentes de cada hashtag via API.</p>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <Hash size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                      <p className="text-gray-500 dark:text-gray-400">Nenhum post encontrado</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                <Hash size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Selecione uma hashtag para começar</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {notaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setNotaModal(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Nota</h3>
            <textarea value={notaText} onChange={(e) => setNotaText(e.target.value)} rows={4} placeholder="Adicione uma observação..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm mb-3" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setNotaModal(null)} className="px-4 py-2 text-gray-600 dark:text-gray-400 text-sm">Cancelar</button>
              <button onClick={saveNota} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
